import { useState, useEffect, useCallback } from 'react';
import { 
  fetchExchanges, 
  fetchExchangeStats 
} from '../controller/exchange-controller';

export const useExchangeViewModel = () => {
  // State for exchanges
  const [exchanges, setExchanges] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 1
  });
  
  // State for status summary
  const [statusSummary, setStatusSummary] = useState({
    total: 0,
    pending: 0,
    processing: 0,
    completed: 0,
    cancelled: 0,
    confirmed: 0,
    shipped: 0,
    returned: 0,
    returnReceived: 0,
    paid: 0
  });
  
  // Filters state
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [syncroFilter, setSyncroFilter] = useState('all');
  
  // Map tab index to status
  const tabToStatus = (tabIndex) => {
    const statuses = ['all', 'pending', 'processing', 'completed'];
    return statuses[tabIndex] || 'all';
  };
  
  // Fetch exchanges data
  const fetchExchangeData = useCallback(async (pageNumber = 1) => {
    try {
      setIsLoading(true);
      
      // Determine status from activeTab if statusFilter is not set directly
      const status = statusFilter !== 'all' ? statusFilter : tabToStatus(activeTab);
      
      const response = await fetchExchanges({
        PageNumber: pageNumber,
        Size: pagination.pageSize,
        Search: searchTerm,
        Status: status === 'all' ? '' : status,
        DateFrom: startDate,
        DateTo: endDate,
        Syncro: syncroFilter === 'all' ? '' : syncroFilter
      });
      
      setExchanges(response.data);
      setPagination({
        currentPage: response.currentPage,
        pageSize: response.pageSize,
        totalItems: response.totalItems,
        totalPages: response.totalPages
      });
    } catch (error) {
      console.error('Error in fetchExchangeData:', error);
      setExchanges([]);
    } finally {
      setIsLoading(false);
    }
  }, [activeTab, pagination.pageSize, searchTerm, statusFilter, startDate, endDate, syncroFilter]);
  
  // Fetch status summary
  const fetchStatusSummary = useCallback(async () => {
    try {
      const stats = await fetchExchangeStats();
      setStatusSummary(stats);
    } catch (error) {
      console.error('Error fetching status summary:', error);
    }
  }, []);
  
  // Initial load
  useEffect(() => {
    fetchExchangeData(1);
    fetchStatusSummary();
  }, [fetchExchangeData, fetchStatusSummary]);
  
  // Handle tab change
  const handleTabChange = (index) => {
    setActiveTab(index);
    setStatusFilter(tabToStatus(index));
    fetchExchangeData(1);
  };
  
  // Handle page change
  const handlePageChange = (pageNumber) => {
    fetchExchangeData(pageNumber);
  };

  // Handle page size change
  const handlePageSizeChange = (newSize) => {
    setPagination(prev => ({ ...prev, pageSize: newSize }));
    fetchExchangeData(1);
  };
  
  // Handle status filter change from summary cards
  const handleStatusFilterChange = (status) => {
    setStatusFilter(status);
    // Set activeTab to matching tab if possible
    const statusToTabIndex = {
      'all': 0,
      'pending': 1,
      'processing': 2,
      'completed': 3
    };
    if (statusToTabIndex[status] !== undefined) {
      setActiveTab(statusToTabIndex[status]);
    }
    fetchExchangeData(1);
  };
  
  // Apply all filters
  const applyFilters = () => {
    fetchExchangeData(1);
  };
  
  // Refresh data
  const refreshExchangeData = () => {
    fetchExchangeData(pagination.currentPage);
  };
  
  // Refresh dashboard
  const refreshStatusSummary = () => {
    fetchStatusSummary();
  };
  
  // Fetch data for header
  const fetchHeaderData = () => {
    fetchExchangeData(1);
    fetchStatusSummary();
  };

  return {
    exchanges,
    isLoading,
    statusSummary,
    pagination,
    activeTab,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    syncroFilter,
    setSyncroFilter,
    handleTabChange,
    handlePageChange,
    handlePageSizeChange,
    handleStatusFilterChange,
    applyFilters,
    fetchHeaderData,
    refreshExchangeData,
    refreshStatusSummary
  };
};